import type { IStandardErrorResponse } from 'src/api/common';
import { useMutation, useQueryClient } from 'react-query';

import type { IPlaybookLeadFilterParams, IPlaybookV2 } from 'src/services/api/playbooks/types';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { playbooksPath } from 'src/services/api/playbooks/path';
import { getApiErrorMessage } from 'src/services/api/utils';
import { ELIGIBLE_ROWS_QUERY_KEY } from 'src/services/api/playbooks/index';

/**
 * Mirrors UpdatePlaybookRequestSchema in landlord_playbooks_api.py.
 * The backend forbids extra keys, so only send what should change.
 */
export type IUpdatePlaybookRequest = {
    title?: string | null;
    description?: string;
    is_enabled?: boolean;
    property_id?: number;
    lead_filter_params?: IPlaybookLeadFilterParams | null;
    trigger?: {
        run_limit_per_lead?: number;
        params?: Record<string, unknown>;
    };
};

export const useUpdatePlaybook = (id?: number | null) => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<IPlaybookV2, IStandardErrorResponse, IUpdatePlaybookRequest>(
        async (payload) => {
            const res = await apiClient(`${playbooksPath}/${id}`, {
                method: 'patch',
                json: payload,
            }).json<{ playbook: IPlaybookV2 }>();

            return res.playbook;
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.playbooksV2);
                queryClient.invalidateQueries(`${ELIGIBLE_ROWS_QUERY_KEY}-${id}`);
                // Toast fires here (step 2) so it's suppressed if duplicate validation fails.
                addNotification('Playbook updated', 'success');
            },
            onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to update playbook';
                addNotification(errorMessage, 'error');
            }
        }
    );
};

type IUpdatePlaybookV2Request = Pick<IPlaybookV2, 'first_step'>;

export const useUpdatePlaybookV2 = (id?: number | null) => {
    const { addNotification } = useNotifications();

    return useMutation<IPlaybookV2, IStandardErrorResponse, IUpdatePlaybookV2Request>(
        async (payload) => {
            const res = await apiClient(`${playbooksPath}/${id}/steps`, {
                method: 'put',
                json: payload
            }).json<{ playbook: IPlaybookV2 }>();

            return res.playbook;
        }, {
            onSuccess: () => {
                // Don't invalidate the queries here because we have to update the playbook title
                // separately - they are invalidated in `useUpdatePlaybook` instead.
            },
            onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to update playbook';
                addNotification(errorMessage, 'error');
            }
        }
    );
};
